import { TFileSchema } from '@conversed/shared/src/central/types/common.type';
import { Types } from 'mongoose';
import { z } from 'zod';
import {
  DocumentType,
  ThirdPartySource,
  TrainingStatus,
} from './data-sources.enums';

export const LLMBlockSelectionSchema = z.object({
  blockId: z.string(),
  companyId: z.string(),
});

export const DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM = false;
export const DATA_SOURCES_S3_BASE_PATH = 'data-sources';
export const TRAIN_AI_XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const DataSourceDataSchema = z.object({
  name: z.string().optional(),
  data: TFileSchema.optional(),
  documentType: z.enum(
    Object.values(DocumentType) as [DocumentType, ...DocumentType[]],
  ),
  // * Set to optional for backwards compatibility
  canBeDownloaded: z.boolean().optional(),
  label: z.string().optional(),
  trainingStatus: z
    .enum(
      Object.values(TrainingStatus) as [TrainingStatus, ...TrainingStatus[]],
    )
    .optional(),
  sharedWithCompanyIds: z.array(z.string()).optional(),
  folderId: z.string().optional(),
  expiryDate: z.string().optional(),
});

export const DuplicateQuestionSchema = z.object({
  question: z.string(),
  indices: z.array(z.number()),
});

export const DataSourceDetailsSchema = z.object({
  _id: z.union([z.instanceof(Types.ObjectId), z.string()]).optional(),
  name: z.string().optional(),
  key: z.string(),
  companyId: z.string(),
  createdBy: z.string(),
  createdByName: z.string().optional(),
  fileType: z.string(),
  fileSize: z.number(),
  documentType: z.enum(
    Object.values(DocumentType) as [DocumentType, ...DocumentType[]],
  ),
  trainingStatus: z
    .enum(
      Object.values(TrainingStatus) as [TrainingStatus, ...TrainingStatus[]],
    )
    .optional(),
  duplicateQuestions: z.array(DuplicateQuestionSchema).optional(),
  lastSuccessfulTrainingStartedAt: z.string().optional(),
  createdAt: z.union([z.date(), z.string()]).optional(),
  updatedAt: z.union([z.date(), z.string()]).optional(),
  automaticallyIncludeInLLM: z.boolean().optional(),
  pseudonymized: z.boolean().optional(),
  thirdPartySource: z
    .enum(
      Object.values(ThirdPartySource) as [
        ThirdPartySource,
        ...ThirdPartySource[],
      ],
    )
    .nullable()
    .optional(),
  lastThirdPartySync: z.string().nullable().optional(),
  sharedWithCompanyIds: z.array(z.string()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  label: z.string().optional(),
  rankingTier: z.number().nullable().optional(),
  canBeDownloaded: z.boolean().optional(),
  folderId: z.string().nullable().optional(),
  folderPath: z.array(z.string()).optional(),
  expiryDate: z.union([z.date(), z.string()]).nullable().optional(),
  daysUntilExpiry: z.number().optional(),
});

export const MoveDataSourceSchema = z.object({
  folderId: z.string().nullable(),
  expiryDate: z.string().nullable().optional(),
});

export const UpdateDataSourceSchema = DataSourceDetailsSchema.pick({
  name: true,
  key: true,
  trainingStatus: true,
  lastSuccessfulTrainingStartedAt: true,
  thirdPartySource: true,
  lastThirdPartySync: true,
  sharedWithCompanyIds: true,
  metadata: true,
  label: true,
  canBeDownloaded: true,
  folderId: true,
  expiryDate: true,
  pseudonymized: true,
});

export const TrainDataSourceSchema = DataSourceDetailsSchema.pick({
  key: true,
  documentType: true,
  lastSuccessfulTrainingStartedAt: true,
  automaticallyIncludeInLLM: true,
  fileType: true,
  thirdPartySource: true,
  label: true,
  name: true,
}).extend({
  selectedLLMBlocks: LLMBlockSelectionSchema.array().optional(),
});

export const TrainingFreshnessSchema = z.object({
  botName: z.string().optional(),
  lastTrainedAt: z.string().nullable(),
  thresholds: z.object({
    warning: z.number(),
    urgent: z.number(),
    critical: z.number(),
  }),
});

// Updated to be a discriminated union based on document type
export const DataSourceFileContentSchema = z.union([
  // FAQ format
  z.object({
    question: z.string(),
    answer: z.string(),
    url: z.string().optional(),
    keywords: z.string().optional(),
    categories: z.string().optional(),
  }),
  // Wiki format
  z.object({
    info: z.string(),
    url: z.string().optional(),
  }),
]);

export const UpdateExistingDataSourceFileSchema = z.object({
  existingFile: DataSourceDetailsSchema,
  data: DataSourceFileContentSchema.array(),
  automaticallyIncludeInLLM: z
    .boolean()
    .default(DEFAULT_AUTOMATICALLY_INCLUDE_IN_LLM),
  documentType: z
    .enum(Object.values(DocumentType) as [DocumentType, ...DocumentType[]])
    .optional(),
  selectedLLMBlocks: LLMBlockSelectionSchema.array().optional(),
});
